import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { setTheme } from 'app/providers/themeSlice';
import { useAuth } from '../contexts/AuthContext';
import { useStudentProfile } from './useStudentProfile';
import { updateProfileSettings } from '../utils/studentProfileStorage';
import { resolveThemePreference } from '../utils/userProfilePreferences';

const ParashariPreferencesContext = createContext(null);

/**
 * Sync platform theme with student profile preferences.
 * @returns {{
 *   isDark: boolean;
 *   applyThemePreference: (themePreference: 'dark' | 'light', options?: { persist?: boolean }) => Promise<void>;
 *   handleToggleTheme: () => void;
 * }}
 */
export function useParashariPreferencesSync() {
  const dispatch = useAppDispatch();
  const resolved = useAppSelector((s) => s.theme.resolved);
  const { user, userId, loading: authLoading } = useAuth();
  const { profile } = useStudentProfile();

  const applyThemePreference = useCallback(
    async (themePreference, { persist = true } = {}) => {
      dispatch(setTheme(themePreference));
      if (persist && userId) {
        await updateProfileSettings({ themePreference });
      }
    },
    [dispatch, userId],
  );

  const handleToggleTheme = useCallback(() => {
    const next = resolved === 'light' ? 'dark' : 'light';
    void applyThemePreference(next);
  }, [resolved, applyThemePreference]);

  const themePreference = resolveThemePreference(profile);

  useEffect(() => {
    if (authLoading || !user) return;
    if (themePreference !== resolved) {
      dispatch(setTheme(themePreference));
    }
  }, [authLoading, user, themePreference, resolved, dispatch]);

  return useMemo(
    () => ({
      isDark: resolved === 'dark',
      applyThemePreference,
      handleToggleTheme,
    }),
    [resolved, applyThemePreference, handleToggleTheme],
  );
}

export function ParashariPreferencesProvider({ children }) {
  const value = useParashariPreferencesSync();
  return (
    <ParashariPreferencesContext.Provider value={value}>{children}</ParashariPreferencesContext.Provider>
  );
}

export function useParashariPreferences() {
  const ctx = useContext(ParashariPreferencesContext);
  if (!ctx) {
    throw new Error('useParashariPreferences must be used within ParashariPreferencesProvider');
  }
  return ctx;
}

export function useParashariPreferencesOptional() {
  return useContext(ParashariPreferencesContext);
}
